import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardHeader, CardContent } from '../../components/ui';
import { Button, Badge } from '../../components/ui';
import { PageHeader } from '@/components/layout/PageHeader';
import { adminBackofficeApi } from '../../services/adminBackoffice';
import { Loader2 } from 'lucide-react';

export default function RiskDetailPage() {
  const { academyId } = useParams<{ academyId: string }>();
  const [profile, setProfile] = useState<any>(null);
  const [assessment, setAssessment] = useState<any>(null);
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!academyId) return;
    Promise.all([
      adminBackofficeApi.getRiskProfile(academyId),
      adminBackofficeApi.getRiskEvents({ academyId }),
    ])
      .then(([profileRes, eventsRes]) => {
        if (profileRes.success && profileRes.data) {
          setProfile(profileRes.data.profile);
          setAssessment(profileRes.data.assessment);
        }
        if (eventsRes.success && eventsRes.data) setEvents(eventsRes.data.events ?? []);
      })
      .catch((err) => setError(err?.response?.data?.error || err?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [academyId]);

  const handleRunAssessment = () => {
    if (!academyId) return;
    setLoading(true);
    adminBackofficeApi
      .runRiskAssessment(academyId)
      .then((res) => res.success && res.data && setAssessment(res.data))
      .catch((err) => setError(err?.response?.data?.error || err?.message || 'Failed'))
      .finally(() => setLoading(false));
  };

  if (loading && !profile) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-gray-500" />
      </div>
    );
  }

  if (error && !profile) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20 p-4 text-red-700 dark:text-red-300">
        {error}
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={`Risk profile: ${profile?.academyId ?? academyId}`}
        breadcrumbs={[{ label: 'Risk', href: '/backoffice/risk' }]}
        help={<p>View risk level, balance, dispute rate, and events for this academy.</p>}
      />

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <h2 className="text-lg font-medium">Profile</h2>
            <Button size="sm" onClick={handleRunAssessment} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Run assessment'}
            </Button>
          </CardHeader>
          <CardContent>
            {profile && (
              <dl className="space-y-2 text-sm">
                <div>
                  <dt className="text-gray-500">Risk level</dt>
                  <dd><Badge variant={profile.riskLevel === 'high' || profile.riskLevel === 'restricted' ? 'destructive' : 'secondary'}>{profile.riskLevel}</Badge></dd>
                </div>
                <div>
                  <dt className="text-gray-500">Current balance</dt>
                  <dd>{(profile.currentBalance / 100).toFixed(2)}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Dispute rate</dt>
                  <dd>{(profile.disputeRate * 100).toFixed(2)}%</dd>
                </div>
              </dl>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <h2 className="text-lg font-medium">Assessment</h2>
          </CardHeader>
          <CardContent>
            {assessment && (
              <dl className="space-y-2 text-sm">
                <div>
                  <dt className="text-gray-500">Level</dt>
                  <dd>{assessment.riskLevel}</dd>
                </div>
                {assessment.reasons?.length > 0 && (
                  <div>
                    <dt className="text-gray-500">Reasons</dt>
                    <dd className="list-disc list-inside">{assessment.reasons.join(', ')}</dd>
                  </div>
                )}
              </dl>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="mt-6">
        <CardHeader>
          <h2 className="text-lg font-medium">Risk events</h2>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700">
                  <th className="text-left py-2 font-medium">Type</th>
                  <th className="text-left py-2 font-medium">Severity</th>
                  <th className="text-left py-2 font-medium">Created</th>
                </tr>
              </thead>
              <tbody>
                {events.map((e) => (
                  <tr key={e.id} className="border-b border-gray-100 dark:border-gray-800">
                    <td className="py-2">{e.eventType}</td>
                    <td className="py-2">{e.severity}</td>
                    <td className="py-2">{e.createdAt ? new Date(e.createdAt).toLocaleString() : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {events.length === 0 && <p className="py-4 text-center text-gray-500">No events.</p>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
